import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { format } from "date-fns";
import instance from "api/globalaxios";
import { useSnackbar } from "contexts/SnackbarContext";

const useStyles = makeStyles(() => ({
  caseCard: {
    padding: "0.75rem 1rem",
    backgroundColor: "#f5f5f5",
    borderRadius: 6,
    border: "1px solid #e0e0e0",
    marginBottom: "1.25rem",
  },
  caption: {
    fontSize: "12px",
    color: "#4F4F4F",
  },
  readOnlyValue: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#252733",
  },
  hint: {
    marginTop: "1rem",
    fontSize: "12px",
    color: "#4F4F4F",
  },
  error: {
    marginTop: "1rem",
    fontSize: "14px",
    color: "#D32F2F",
  },
}));

export const PAYMENT_MODES = [
  { value: "bank_transfer", label: "Payment by Bank Transfer" },
  { value: "payment_gateway", label: "Payment by Payment Gateway" },
];

export const modeLabel = (paymentMode) =>
  PAYMENT_MODES.find((mode) => mode.value === (paymentMode))?.label || '—';

// date_of_payment comes back as a UTC datetime; the date input needs the
// local calendar day.
const toDateInput = (paymentDateTime) => {
  const paymentDate = paymentDateTime ? new Date(paymentDateTime) : null;
  return paymentDate && !isNaN(paymentDate)
    ? format(paymentDate, "yyyy-MM-dd")
    : "";
};

const toAmountInput = (amount) =>
  amount != null && amount !== "" ? String(amount) : "";

const getErrorMessage = (errorResponse) => {
  if (errorResponse?.detail || errorResponse?.error || errorResponse?.message) {
    return errorResponse.detail || errorResponse.error || errorResponse.message;
  }
  // DRF field errors, e.g. { amount_recieved: ["A valid number is required."] }
  const fieldError =
    errorResponse && Object.values(errorResponse).find(Array.isArray);
  return (
    fieldError?.[0] || "Failed to save payment details. Please try again."
  );
};

export default function EditPaymentDetailsDialog({
  open,
  invoice,
  onClose,
  onSaved,
}) {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  const [referenceNo, setReferenceNo] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const today = format(new Date(), "yyyy-MM-dd");

  // Re-seed every time a different row is opened.
  useEffect(() => {
    if (!invoice) return;
    setReferenceNo(invoice.transaction_reference_number || "");
    setReceiptDate(toDateInput(invoice.date_of_payment));
    setAmountReceived(toAmountInput(invoice.amount_recieved));
    setPaymentMode(invoice.payment_mode || "");
    setError("");
  }, [invoice]);

  const handleSave = () => {
    setError("");

    // Send only the fields that changed: once a field has a value only an
    // admin may edit it, so resending an untouched filled field would be
    // rejected with a 403 even when the user is just adding a missing one.
    const changedPaymentFields = {};

    const trimmedReferenceNo = referenceNo.trim();
    if (trimmedReferenceNo !== (invoice.transaction_reference_number || "")) {
      if (trimmedReferenceNo === "") {
        setError("Transaction Reference No cannot be cleared.");
        return;
      }
      changedPaymentFields.transaction_reference_number = trimmedReferenceNo;
    }

    if (receiptDate !== toDateInput(invoice.date_of_payment)) {
      if (!receiptDate) {
        setError("Date of Payment Receipt cannot be cleared.");
        return;
      }
      if (receiptDate > today) {
        setError("Date of Payment Receipt cannot be in the future.");
        return;
      }
      changedPaymentFields.date_of_payment = `${receiptDate}T00:00:00`;
    }

    const trimmedAmountReceived = amountReceived.trim();
    if (trimmedAmountReceived !== toAmountInput(invoice.amount_recieved)) {
      if (trimmedAmountReceived === "") {
        setError("Amount received cannot be cleared.");
        return;
      }
      if (
        !/^\d+(\.\d{1,2})?$/.test(trimmedAmountReceived) ||
        parseFloat(trimmedAmountReceived) <= 0
      ) {
        setError(
          "Please enter a valid amount received, upto 2 decimal points."
        );
        return;
      }
      changedPaymentFields.amount_recieved = trimmedAmountReceived;
    }

    if (paymentMode !== (invoice.payment_mode || "")) {
      changedPaymentFields.payment_mode = paymentMode;
    }

    if (Object.keys(changedPaymentFields).length === 0) {
      setError("No changes to save.");
      return;
    }

    setSaving(true);
    updateInvoicePaymentDetails(invoice.id, changedPaymentFields)
      .then(() => {
        showSnackbar("Payment details saved.", "success");
        if (onSaved) onSaved();
        onClose();
      })
      .catch((saveError) => {
        setError(getErrorMessage(saveError?.response?.data));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add/Edit Payment Details</DialogTitle>
      <DialogContent dividers>
        {invoice && (
          <Box className={classes.caseCard}>
            <Typography className={classes.caption}>
              Invoice {invoice.invoice_number || "—"} · Case{" "}
              {invoice.disputeId || "—"}
            </Typography>
            <Typography className={classes.readOnlyValue}>
              {invoice.full_name_of_user || "—"}
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              label="Transaction Reference No"
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="date"
              label="Date of Payment Receipt"
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              label="Amount received (Rs)"
              value={amountReceived}
              onChange={(e) => setAmountReceived(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="payment-mode-label">Mode</InputLabel>
              <Select
                labelId="payment-mode-label"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                label="Mode"
              >
                {PAYMENT_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography className={classes.hint}>
          Fill in the details you have; the rest can be added later. Once a
          detail is saved, only an admin can change it.
        </Typography>

        {error && <Typography className={classes.error}>{error}</Typography>}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px 24px" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          style={{
            background: "#2F80ED",
            color: "white",
            textTransform: "none",
            padding: "6px 16px",
          }}
        >
          {saving ? (
            <CircularProgress size={20} style={{ color: "white" }} />
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

async function updateInvoicePaymentDetails(invoiceId, changedPaymentFields) {
  return await instance.patch(
    `/surepay/invoice_paymentlink_dashboard/${invoiceId}/`,
    changedPaymentFields
  );
}
